import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Badge, Button, Card, Input, Modal, Progress, Select, Space, Table, Tag,
  Tooltip, Typography, message,
} from 'antd';
import {
  BarChartOutlined, PauseCircleOutlined, PlayCircleOutlined, SearchOutlined,
  SolutionOutlined, ThunderboltOutlined, MessageOutlined, PaperClipOutlined,
  NumberOutlined, RedoOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { useTheme } from '../../context/ThemeContext';
import { API_BASE_URL } from '../../utils/config';
import EnerjiAdimModal, { AboneListesiAlani } from './EnerjiAdimModal';
import EnerjiGorevKabulModal from './EnerjiGorevKabulModal';
import EnerjiGantt from './EnerjiGantt';
import EnerjiNotlarModal from './EnerjiNotlarModal';
import { terminIpucu } from '../../utils/termin';

const { Title } = Typography;

const authHeaders = () => ({
  headers: {
    ...(localStorage.getItem('auth_token') && {
      Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
    }),
  },
});

// Adım hücresindeki Tag'in rengi. Alt durum varsa etiket olarak o gösterilir.
const HUCRE_RENK = {
  tamamlandi: 'green',
  aktif: 'processing',
  bekliyor: 'default',
  kapsam_disi: null,
};

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime()) || d.getFullYear() < 1990) return '-';
  return d.toLocaleDateString('tr-TR');
};

/**
 * Enerji Süreçleri — TEK EKRAN.
 *
 * Satır = lokasyon. Her enerji adımı için bir sütun; hücredeki Tag'e tıklanınca
 * o adımın durumu değiştirilir / verisi girilir. Adım ve durum tanımları
 * backend'den (`GET /enerji/liste` → tanim) gelir, burada sabit liste yoktur.
 */
/**
 * @param {boolean} gomulu - Sekme içinde gösteriliyorsa dış dolgu/başlık kırpılır
 *   (İnşaat & Enerji sayfası zaten kendi başlığını basıyor).
 */
const EnerjiSurecPage = ({ gomulu = false }) => {
  const { darkMode } = useTheme();
  const [satirlar, setSatirlar] = useState([]);
  const [tanim, setTanim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hata, setHata] = useState('');
  const [arama, setArama] = useState('');
  const [durumFiltre, setDurumFiltre] = useState('Tümü');

  const [adimModal, setAdimModal] = useState(null); // {satir, adim}
  const [kabulSatir, setKabulSatir] = useState(null);
  // Gantt lokasyon bazlı: her satırın kendi zaman çizelgesi açılır.
  const [ganttSatir, setGanttSatir] = useState(null);
  // Red sebebi ve Notlar girisi bu detay modalinde; onceden hicbir yerden
  // acilmadigi icin ikisi de kullaniciya ulasmiyordu.
  const [notSatir, setNotSatir] = useState(null);
  // 20.08 madde 2/13: muayene sonrası abone/sayaç düzenleme
  const [aboneSatir, setAboneSatir] = useState(null);
  const [aboneListesi, setAboneListesi] = useState([]);
  const [aboneKabinSayisi, setAboneKabinSayisi] = useState(0);
  const [aboneKaydediyor, setAboneKaydediyor] = useState(false);

  const getir = useCallback(async () => {
    setLoading(true);
    setHata('');
    try {
      const res = await axios.get(`${API_BASE_URL}/enerji/liste`, authHeaders());
      setSatirlar(res.data?.satirlar || []);
      setTanim(res.data?.tanim || null);
    } catch (e) {
      setHata(e?.response?.data?.detail || 'Enerji süreçleri yüklenemedi.');
      setSatirlar([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { getir(); }, [getir]);

  const beklet = async (satir) => {
    setKabulSatir({ ...satir, _mod: 'beklet' });
  };

  const devamEt = async (satir) => {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/enerji/${satir.lokasyon}/devam`, {}, authHeaders()
      );
      message.success(`Süreç devam ediyor (${res.data?.beklenen_gun ?? 0} gün beklendi).`);
      getir();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Devam ettirilemedi.');
    }
  };

  const aboneAc = async (satir) => {
    setAboneSatir(satir);
    setAboneListesi([]);
    setAboneKabinSayisi(0);
    try {
      const r = await axios.get(`${API_BASE_URL}/enerji/${satir.lokasyon}`, authHeaders());
      setAboneKabinSayisi(Number(r.data?.kabin_sayisi) || 0);
      setAboneListesi(Array.isArray(r.data?.aboneler) ? r.data.aboneler : []);
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Abone bilgileri alınamadı.');
    }
  };
  const aboneKaydet = async () => {
    if (!aboneListesi.some((r) => (r?.abone_no || '').trim())) {
      message.warning('En az bir kabin için abone numarası girin.');
      return;
    }
    setAboneKaydediyor(true);
    try {
      await axios.put(
        `${API_BASE_URL}/enerji/${aboneSatir.lokasyon}/aboneler`,
        { aboneler: aboneListesi },
        authHeaders()
      );
      message.success('Abone / sayaç numaraları kaydedildi.');
      setAboneSatir(null);
      getir();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Kaydedilemedi.');
    } finally {
      setAboneKaydediyor(false);
    }
  };
  const yenidenAc = (satir) => {
    Modal.confirm({
      title: 'Süreç yeniden açılsın mı?',
      content: 'Reddedilmiş/iptal edilmiş süreç kaldığı adımdan devam eder. Yalnızca yönetim yapabilir.',
      okText: 'Yeniden Aç',
      cancelText: 'Vazgeç',
      onOk: async () => {
        try {
          await axios.post(`${API_BASE_URL}/enerji/${satir.lokasyon}/yeniden-ac`, {}, authHeaders());
          message.success('Süreç yeniden açıldı.');
          getir();
        } catch (e) {
          message.error(e?.response?.data?.detail || 'Yeniden açılamadı.');
        }
      },
    });
  };
  const kapali = (s) => Boolean(s.reddedildi) || ['reddedildi', 'iptal'].includes(s.surec_durumu);

  const filtreli = useMemo(() => {
    let veri = satirlar;
    if (durumFiltre === 'Kabul Bekleyen') veri = veri.filter((s) => !s.kayit_var);
    else if (durumFiltre === 'Beklemede') veri = veri.filter((s) => s.kayit_var && s.beklemede && !kapali(s));
    else if (durumFiltre === 'Devam Eden') veri = veri.filter((s) => s.kayit_var && !s.tamamlandi && !s.beklemede && !kapali(s));
    else if (durumFiltre === 'Tamamlanan') veri = veri.filter((s) => s.kayit_var && s.tamamlandi);
    else if (durumFiltre === 'Reddedilen') veri = veri.filter((s) => s.kayit_var && kapali(s));
    if (arama.trim()) {
      const q = arama.trim().toLocaleLowerCase('tr');
      veri = veri.filter((s) =>
        (s.lokasyon_adi || '').toLocaleLowerCase('tr').includes(q) ||
        (s.proje || '').toLocaleLowerCase('tr').includes(q) ||
        String(s.lokasyon || '').includes(q)
      );
    }
    return veri;
  }, [satirlar, durumFiltre, arama]);

  // Adım sütunları backend tanımından üretilir (karar adımı hariç).
  const adimSutunlari = useMemo(() => {
    if (!tanim?.adimlar) return [];
    return tanim.adimlar
      .filter((a) => !a.karar_adimi)
      .map((adim) => ({
        title: (
          <Tooltip title={`Termin: ${adim.termin_gun} gün`}>
            <span>{adim.ad}</span>
          </Tooltip>
        ),
        key: adim.anahtar,
        width: 150,
        render: (_, satir) => {
          const hucre = satir.adim_durumlari?.[adim.anahtar];
          if (!hucre || hucre.durum === 'kapsam_disi') {
            return <span style={{ color: '#bfbfbf' }}>—</span>;
          }
          const durumEtiket =
            (adim.durumlar || []).find((d) => d.deger === hucre.alt_durum)?.etiket ||
            hucre.alt_durum ||
            { tamamlandi: 'Tamamlandı', aktif: 'İşlemde', bekliyor: 'Bekliyor' }[hucre.durum];
          return (
            <Tooltip
              title={
                <>
                  <div><strong>{adim.ad}</strong></div>
                  {hucre.firma?.firma_adi && <div>Firma: {hucre.firma.firma_adi}</div>}
                  {hucre.dosya?.var && <div>📎 {hucre.dosya.dosya_adi || 'Ruhsat dosyası yüklü'}</div>}
                  {terminIpucu(
                    [
                      ['Adım başlangıcı', hucre.durum === 'aktif' ? satir.adim_baslangic_tarihi : null],
                      [hucre.durum === 'tamamlandi' ? 'Tamamlandı' : 'Adım tarihi', hucre.tarih],
                    ],
                    adim.termin_gun,
                    0,
                  )}
                  <div style={{ marginTop: 4, opacity: 0.75 }}>Tıklayarak durumu güncelleyin</div>
                </>
              }
            >
              <Tag
                color={HUCRE_RENK[hucre.durum]}
                style={{ cursor: 'pointer', margin: 0 }}
                onClick={() => setAdimModal({ satir, adim: adim.anahtar })}
              >
                {durumEtiket}
                {hucre.dosya?.var && <PaperClipOutlined style={{ marginLeft: 4 }} />}
              </Tag>
              {hucre.tarih && (
                <div style={{ fontSize: 10, color: '#8c8c8c' }}>{formatDate(hucre.tarih)}</div>
              )}
              {hucre.firma?.firma_adi && (
                <div style={{ fontSize: 10, color: '#5b6b9a', maxWidth: 140, overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  🏢 {hucre.firma.firma_adi}
                </div>
              )}
            </Tooltip>
          );
        },
      }));
  }, [tanim]);

  const columns = useMemo(() => [
    {
      title: 'Lokasyon',
      dataIndex: 'lokasyon_adi',
      key: 'lokasyon',
      width: 220,
      fixed: 'left',
      render: (text, satir) => (
        <div>
          <strong>{text || satir.lokasyon}</strong>
          <div style={{ fontSize: 11, color: '#8c8c8c' }}>{satir.lokasyon}</div>
        </div>
      ),
    },
    {
      title: 'Proje',
      dataIndex: 'proje',
      key: 'proje',
      width: 140,
      render: (v) => v || '-',
    },
    {
      title: 'Kurulum',
      dataIndex: 'kurulum_tarihi',
      key: 'kurulum',
      width: 95,
      render: (v) => (
        <Tooltip title="Kabin kurulum tarihi — enerji termini bu tarihten başlar">
          <span>{formatDate(v)}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Enerji Tipi',
      dataIndex: 'enerji_tipi',
      key: 'tip',
      width: 100,
      render: (v) => (v
        ? <Tag color={v === 'suzme' ? 'purple' : 'blue'}>{v === 'suzme' ? 'Süzme' : 'Abonelik'}</Tag>
        : <Tag>Seçilmedi</Tag>),
    },
    {
      title: 'İlerleme',
      key: 'ilerleme',
      width: 150,
      render: (_, s) => (
        <Tooltip title={`${s.tamamlanan_adim}/${s.adim_sayisi} adım · toplam termin ${s.toplam_termin_gun} gün`}>
          <Progress
            percent={s.adim_sayisi ? Math.round((s.tamamlanan_adim / s.adim_sayisi) * 100) : 0}
            size="small"
            status={s.beklemede ? 'exception' : (s.tamamlandi ? 'success' : 'active')}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Toplam Termin',
      dataIndex: 'toplam_termin_gun',
      key: 'termin',
      width: 120,
      sorter: (a, b) => (a.toplam_termin_gun || 0) - (b.toplam_termin_gun || 0),
      render: (v, s) => (
        <span>
          {/* Sayacın hangi tarihten başladığı görünsün diye ipucunda tarihler listelenir. */}
          <Tooltip
            title={terminIpucu(
              [
                ['Görev alındı', s.gorev_alinma],
                ['Görev kabul', s.gorev_kabul_tarihi],
                ['Aktif adım başlangıcı', s.adim_baslangic_tarihi],
                ['Beklemeye alındı', s.bekleme_baslangic],
              ],
              v,
              0,
            )}
          >
            <span style={{ borderBottom: '1px dotted #bfbfbf' }}>{v} gün</span>
          </Tooltip>
          {s.bekleme_gun > 0 && (
            <Tooltip title="Beklemede geçen süre terminden düşülür">
              <Tag color="gold" style={{ marginLeft: 4 }}>+{s.bekleme_gun}g bekleme</Tag>
            </Tooltip>
          )}
        </span>
      ),
    },
    ...adimSutunlari,
    {
      title: 'Durum',
      key: 'genelDurum',
      width: 130,
      fixed: 'right',
      render: (_, s) => {
        if (!s.kayit_var) return <Tag color="default">Görev Kabul Bekliyor</Tag>;
        if (kapali(s)) {
          const redEtiket = (tanim?.red_sebepleri || []).find((r) => r.deger === s.red_sebebi)?.etiket
            || s.red_sebebi || '';
          return (
            <Tooltip title={`${redEtiket}${s.red_aciklama ? ` — ${s.red_aciklama}` : ''}${s.red_eden_adi ? ` (${s.red_eden_adi}` : ''}${s.red_tarihi ? `, ${formatDate(s.red_tarihi)})` : (s.red_eden_adi ? ')' : '')}`}>
              <Tag color="red">{s.surec_durumu === 'iptal' ? 'İptal' : 'Reddedildi'}</Tag>
            </Tooltip>
          );
        }
        if (s.beklemede) {
          const sebepEtiket = (tanim?.bekleme_sebepleri || []).find((b) => b.deger === s.bekleme_sebebi)?.etiket
            || s.bekleme_sebebi || '';
          return (
            <Tooltip title={`${sebepEtiket} — ${s.bekleme_aciklama || ''}${s.bekleme_sorumlu_adi ? ` · Görevli: ${s.bekleme_sorumlu_adi}` : ''}`}>
              <Tag color="warning">Beklemede{s.bekleme_sorumlu_adi ? ` · ${s.bekleme_sorumlu_adi.split(' ')[0]}` : ''}</Tag>
            </Tooltip>
          );
        }
        if (s.tamamlandi) return <Tag color="green">Tamamlandı</Tag>;
        return <Tag color="blue">{s.aktif_adim_ad}</Tag>;
      },
    },
    {
      title: 'İşlem',
      key: 'islem',
      width: 150,
      fixed: 'right',
      render: (_, s) => (
        <Space size={0}>
          <Badge count={s.not_sayisi || 0} size="small" offset={[-4, 4]}>
            <Button
              type="text"
              icon={<MessageOutlined />}
              title="Notlar — adım bazlı"
              onClick={() => setNotSatir(s)}
            />
          </Badge>
          <Button
            type="text"
            icon={<BarChartOutlined />}
            title="Bu lokasyonun Gantt grafiği"
            disabled={!s.kayit_var}
            onClick={() => setGanttSatir(s)}
          />
          {!kapali(s) && (
            <Button
              type="text"
              icon={<SolutionOutlined />}
              title="Görev kabul — gerekli adımları ve adım firmalarını seç"
              onClick={() => setKabulSatir({ ...s, _mod: 'kabul' })}
            />
          )}
          {s.kayit_var && (
            <Button
              type="text"
              icon={<NumberOutlined />}
              title="Abone / sayaç numaraları (kabin başına)"
              onClick={() => aboneAc(s)}
            />
          )}
          {!s.kayit_var ? null : kapali(s) ? (
            <Button
              type="text"
              icon={<RedoOutlined />}
              title="Süreci yeniden aç (yönetim)"
              onClick={() => yenidenAc(s)}
            />
          ) : s.beklemede ? (
            <Button
              type="text"
              icon={<PlayCircleOutlined />}
              title="Beklemeden devam et"
              onClick={() => devamEt(s)}
            />
          ) : (
            <Button
              type="text"
              icon={<PauseCircleOutlined />}
              title="Beklemeye al (isteğe bağlı: bir kişiye görev ata)"
              onClick={() => beklet(s)}
            />
          )}
        </Space>
      ),
    },
  ], [adimSutunlari, tanim]);

  const sayilar = useMemo(() => ({
    kabulBekleyen: satirlar.filter((s) => !s.kayit_var).length,
    beklemede: satirlar.filter((s) => s.kayit_var && s.beklemede && !kapali(s)).length,
    devam: satirlar.filter((s) => s.kayit_var && !s.tamamlandi && !s.beklemede && !kapali(s)).length,
    tamam: satirlar.filter((s) => s.kayit_var && s.tamamlandi).length,
    red: satirlar.filter((s) => s.kayit_var && kapali(s)).length,
  }), [satirlar]);

  return (
    <div style={{ padding: gomulu ? 0 : 16 }}>
      <Card
        className={darkMode ? 'dark-card' : ''}
        bordered={!gomulu}
        title={
          <Space wrap>
            <ThunderboltOutlined />
            {!gomulu && <Title level={4} style={{ margin: 0 }}>Enerji Süreçleri</Title>}
            <Tag>Kabul bekleyen: {sayilar.kabulBekleyen}</Tag>
            <Tag color="blue">Devam eden: {sayilar.devam}</Tag>
            <Tag color="gold">Beklemede: {sayilar.beklemede}</Tag>
            <Tag color="green">Tamamlanan: {sayilar.tamam}</Tag>
            {sayilar.red > 0 && <Tag color="red">Reddedilen: {sayilar.red}</Tag>}
          </Space>
        }
        extra={
          <Space wrap>
            <Select
              style={{ width: 190 }}
              value={durumFiltre}
              onChange={setDurumFiltre}
              options={[
                { value: 'Tümü', label: `Tümü (${satirlar.length})` },
                { value: 'Kabul Bekleyen', label: `Görev Kabul Bekleyen (${sayilar.kabulBekleyen})` },
                { value: 'Devam Eden', label: `Devam Eden (${sayilar.devam})` },
                { value: 'Beklemede', label: `Beklemede (${sayilar.beklemede})` },
                { value: 'Tamamlanan', label: `Tamamlanan (${sayilar.tamam})` },
                { value: 'Reddedilen', label: `Reddedilen / İptal (${sayilar.red})` },
              ]}
            />
            <Input
              allowClear
              placeholder="Lokasyon / proje ara..."
              prefix={<SearchOutlined />}
              value={arama}
              onChange={(e) => setArama(e.target.value)}
              style={{ width: 220 }}
            />
          </Space>
        }
      >
        {hata && <Alert type="error" showIcon message={hata} style={{ marginBottom: 12 }} />}
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={filtreli}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `Toplam ${t} süreç` }}
        />
      </Card>

      <EnerjiAdimModal
        open={Boolean(adimModal)}
        satir={adimModal?.satir}
        adimAnahtari={adimModal?.adim}
        tanim={tanim}
        onClose={() => setAdimModal(null)}
        onSuccess={getir}
      />

      <EnerjiGorevKabulModal
        open={Boolean(kabulSatir)}
        satir={kabulSatir}
        mod={kabulSatir?._mod}
        tanim={tanim}
        onClose={() => setKabulSatir(null)}
        onSuccess={getir}
      />

      <Modal
        title={`Gantt — ${ganttSatir?.lokasyon_adi || ganttSatir?.lokasyon || ''}`}
        open={Boolean(ganttSatir)}
        onCancel={() => setGanttSatir(null)}
        footer={null}
        width={900}
        destroyOnHidden
      >
        <EnerjiGantt satirlar={ganttSatir ? [ganttSatir] : []} tanim={tanim} />
      </Modal>

      {/* Detay: notlar, red sebebi ve adim isleme. Bu modal daha once hicbir
          yerden acilmiyordu; Notlar ve Enerji Red arayuzu yalnizca burada. */}
      <EnerjiNotlarModal
        open={Boolean(notSatir)}
        record={notSatir}
        adimlar={tanim?.adimlar || []}
        onClose={() => setNotSatir(null)}
        onChanged={getir}
      />

      {/* 20.08 madde 2/13: abone + sayaç numaraları — süreç bittikten sonra da düzenlenebilir */}
      <Modal
        title={`Abone / Sayaç Numaraları — ${aboneSatir?.lokasyon_adi || aboneSatir?.lokasyon || ''}`}
        open={Boolean(aboneSatir)}
        onCancel={() => setAboneSatir(null)}
        onOk={aboneKaydet}
        okText="Kaydet"
        cancelText="Vazgeç"
        okButtonProps={{ loading: aboneKaydediyor }}
        destroyOnHidden
        width={600}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Kabin başına abone ve sayaç numarası"
          description="Muayene sonrası sayaç numaraları buradan eklenebilir. Değerler serbest metindir; lokasyon detayındaki enerji bilgisi de güncellenir."
        />
        <AboneListesiAlani
          value={aboneListesi}
          kabinSayisi={aboneKabinSayisi}
          onChange={setAboneListesi}
        />
      </Modal>
    </div>
  );
};

export default EnerjiSurecPage;
